import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import { fetchAgentMemory, uploadAgentMemory } from '../../../api/gtmEngineer';
import ChatMarkdown from '../../ChatMarkdown/ChatMarkdown';
import {
  primaryActionButtonSx,
  secondaryActionButtonSx
} from '../../modal/ModalActionButtons';

/** Fixed height for the edit / preview body (below tabs). */
const MEMORY_EDITOR_BODY_HEIGHT_PX = 400;

interface AgentMemoryEditorDialogProps {
  open: boolean;
  onClose: () => void;
  campaignId: string | undefined;
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        borderRadius: '12px',
        px: 2,
        py: 1.5,
        border: '1px solid',
        borderColor: theme.palette.error.light,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(211, 47, 47, 0.12)'
            : 'rgba(211, 47, 47, 0.06)'
      })}
    >
      <Typography
        variant="body2"
        sx={(theme) => ({
          color: theme.palette.error.main,
          lineHeight: 1.5
        })}
      >
        {children}
      </Typography>
    </Box>
  );
}

const AgentMemoryEditorDialog: React.FC<AgentMemoryEditorDialogProps> = ({
  open,
  onClose,
  campaignId
}) => {
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [memoryTab, setMemoryTab] = useState<'edit' | 'preview'>('edit');

  useEffect(() => {
    if (!open || !campaignId) return;

    let cancelled = false;
    setMemoryTab('edit');
    setDraft('');
    setLoadError(null);
    setSaveError(null);
    setLoading(true);

    fetchAgentMemory(campaignId)
      .then(({ content }) => {
        if (cancelled) return;
        setDraft(content);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(
            e instanceof Error ? e.message : 'Failed to load agent memory'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, campaignId]);

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    if (!campaignId) return;
    setSaveError(null);
    setSaving(true);
    try {
      await uploadAgentMemory(campaignId, draft);
      onClose();
    } catch (e) {
      setSaveError(
        e instanceof Error ? e.message : 'Failed to save agent memory'
      );
    } finally {
      setSaving(false);
    }
  };

  const canEdit = !loading && !loadError;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.35)',
          backdropFilter: 'blur(6px)'
        }
      }}
      PaperProps={{
        elevation: 0,
        sx: (theme) => ({
          maxHeight: 'min(90vh, 880px)',
          borderRadius: '16px',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 24px 48px rgba(0,0,0,0.45)'
              : '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        })
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pt: 3,
          px: 3,
          pb: 2
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={(theme) => ({
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: theme.customColors?.themeNeutralPrimaryColor
            })}
          >
            Agent memory
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              mt: 0.5,
              color:
                theme.customColors?.themeNeutralSecondaryColor ||
                theme.palette.text.secondary,
              lineHeight: 1.5
            })}
          >
            Markdown notes are the memory the GTM engineer uses to remember
            this campaign's objective.
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={saving}
          aria-label="Close"
          sx={(theme) => ({
            mt: -0.5,
            color: theme.customColors?.themeNeutralSecondaryColor,
            '&:hover': { backgroundColor: 'action.hover' }
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 0,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          minHeight: 0
        }}
      >
        {loading && (
          <Box
            sx={{
              flex: 1,
              minHeight: 320,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              borderRadius: '12px',
              border: '1px dashed',
              borderColor: 'divider',
              backgroundColor: (theme) =>
                theme.customColors?.themeTertiary || 'action.hover'
            }}
          >
            <CircularProgress
              size={36}
              thickness={4}
              sx={(theme) => ({
                color:
                  theme.customColors?.themePrimary ||
                  theme.palette.primary.main
              })}
            />
            <Typography variant="body2" color="text.secondary">
              Loading memory…
            </Typography>
          </Box>
        )}

        {loadError && <ErrorBanner>{loadError}</ErrorBanner>}
        {saveError && <ErrorBanner>{saveError}</ErrorBanner>}

        {canEdit && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : theme.customColors?.themeTertiary || '#f8fafc'
            }}
          >
            <Tabs
              value={memoryTab}
              onChange={(_, v) => setMemoryTab(v)}
              variant="fullWidth"
              sx={(theme) => {
                const dokaiPrimary =
                  theme.customColors?.themePrimary ||
                  theme.palette.primary.main;
                return {
                  minHeight: 44,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(0,0,0,0.15)'
                      : 'rgba(255,255,255,0.5)',
                  '& .MuiTabs-indicator': {
                    backgroundColor: dokaiPrimary
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&.Mui-selected': {
                      color: dokaiPrimary
                    }
                  }
                };
              }}
            >
              <Tab label="Edit" value="edit" />
              <Tab label="Preview" value="preview" />
            </Tabs>

            {memoryTab === 'edit' ? (
              <Box
                sx={{
                  height: MEMORY_EDITOR_BODY_HEIGHT_PX,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0
                }}
              >
                <TextField
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  multiline
                  fullWidth
                  placeholder="Write context, preferences, and facts for the agent…"
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      px: 2,
                      py: 1.5,
                      alignItems: 'flex-start',
                      fontFamily:
                        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                      fontSize: '0.8125rem',
                      lineHeight: 1.65,
                      color: 'text.primary',
                      overflow: 'auto',
                      height: '100%',
                      boxSizing: 'border-box'
                    }
                  }}
                  sx={(theme) => {
                    const dokaiPrimary =
                      theme.customColors?.themePrimary ||
                      theme.palette.primary.main;
                    return {
                      height: '100%',
                      '& .MuiInputBase-root': {
                        height: '100%',
                        alignItems: 'stretch'
                      },
                      '& textarea': {
                        resize: 'none',
                        height: '100% !important',
                        minHeight: '0 !important',
                        boxSizing: 'border-box',
                        overflow: 'auto !important',
                        '&::selection': {
                          backgroundColor: alpha(dokaiPrimary, 0.28)
                        }
                      }
                    };
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={(theme) => {
                  const dokaiPrimary =
                    theme.customColors?.themePrimary ||
                    theme.palette.primary.main;
                  return {
                    height: MEMORY_EDITOR_BODY_HEIGHT_PX,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    boxSizing: 'border-box',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.02)'
                        : theme.palette.background.paper,
                    '& .chatmarkdown-container': {
                      fontSize: '0.875rem',
                      lineHeight: 1.65,
                      color: 'text.primary',
                      '& h1': {
                        fontSize: '1.35rem',
                        fontWeight: 600,
                        mt: 0,
                        mb: 1
                      },
                      '& h2': {
                        fontSize: '1.15rem',
                        fontWeight: 600,
                        mt: 2,
                        mb: 1
                      },
                      '& h3': {
                        fontSize: '1rem',
                        fontWeight: 600,
                        mt: 1.5,
                        mb: 0.75
                      },
                      '& p': { mb: 1 },
                      '& ul, & ol': { pl: 2.5, my: 1 },
                      '& li': { mb: 0.5 },
                      '& pre': {
                        overflow: 'auto',
                        borderRadius: '8px',
                        p: 1.5,
                        bgcolor: 'action.hover',
                        fontSize: '0.8125rem'
                      },
                      '& blockquote': {
                        borderLeft: '3px solid',
                        borderColor: 'divider',
                        pl: 2,
                        my: 1,
                        color: 'text.secondary'
                      },
                      '& a': { color: dokaiPrimary },
                      '& *::selection': {
                        backgroundColor: alpha(dokaiPrimary, 0.28)
                      }
                    }
                  };
                }}
              >
                {draft.trim() ? (
                  <ChatMarkdown>{draft}</ChatMarkdown>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    Nothing to preview yet. Switch to Edit to add markdown.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.02)'
              : theme.palette.background.paper
        }}
      >
        <Button
          variant="text"
          onClick={handleClose}
          disabled={saving}
          sx={secondaryActionButtonSx}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canEdit || saving || !campaignId}
          sx={primaryActionButtonSx}
        >
          {saving ? (
            <CircularProgress size={22} thickness={4} color="inherit" />
          ) : (
            'Save changes'
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AgentMemoryEditorDialog;
